<template>
    <div class="roommate-flow-wrapper">
        <!-- PHONE NUMBER STEP -->
        <div v-if="mode === 'phone'" class="roommate-phone-card">
            <div v-if="phonePromptMessage" class="roommate-phone-message">
                {{ phonePromptMessage }}
            </div>
            <div class="roommate-phone-row">
                <Dropdown v-model="phoneForm.countryCodeId"
                          :options="normalizedCountryCodes"
                          optionValue="value"
                          optionLabel="text"
                          placeholder="Code"
                          filter
                           panelClass="roommate-dropdown-panel"
                          class="roommate-country-dropdown"
                          :disabled="loading || phoneSubmitted"
                          @change="onCountryCodeChange" />
                <input v-model="phoneForm.phoneNumber"
                       type="text"
                       inputmode="numeric"
                       class="roommate-phone-input"
                       :placeholder="phonePlaceholder"
                       :disabled="loading || phoneSubmitted"
                       @input="onPhoneInput"
                       @keyup.enter="submitPhone" />
            </div>
            <div v-if="phoneError" class="roommate-phone-error">
                {{ phoneError }}
            </div>
            <button type="button"
                    class="roommate-phone-submit"
                    :disabled="loading || phoneSubmitted || !isPhoneComplete"
                    @click="submitPhone">
                {{ loading ? 'Please wait...' : 'Continue' }}
            </button>
        </div>
        <!-- PREVIEW -->
        <div v-else-if="mode === 'preview'" class="roommate-card">
            <div class="roommate-card-header">
                <div>
                    <div class="roommate-card-title">{{ entityLabel }} Summary</div>
                    <div class="roommate-card-subtitle">
                        {{ message || 'Please review before adding' }}
                    </div>
                </div>
            </div>
            <div class="roommate-card-body">
                <div class="roommate-row">
                    <span class="roommate-label">First Name</span>
                    <input v-model="editForm.firstName"
                           type="text"
                           class="roommate-edit-input"
                           placeholder="First name"
                           :disabled="loading || submitted"
                           @input="editError = ''" />
                </div>
                <div class="roommate-row">
                    <span class="roommate-label">Last Name</span>
                    <input v-model="editForm.lastName"
                           type="text"
                           class="roommate-edit-input"
                           placeholder="Last name"
                           :disabled="loading || submitted"
                           @input="editError = ''" />
                </div>
                <div class="roommate-row">
                    <span class="roommate-label">Email</span>
                    <input v-model="editForm.email"
                           type="email"
                           class="roommate-edit-input"
                           placeholder="Email"
                           :disabled="loading || submitted"
                           @input="editError = ''" />
                </div>
                <div class="roommate-row">
                    <span class="roommate-label">Country Code</span>
                    <Dropdown v-model="editForm.countryCodeId"
                              :options="normalizedCountryCodes"
                              optionValue="value"
                              optionLabel="text"
                              placeholder="Code"
                              filter
                               panelClass="roommate-dropdown-panel"
                              class="roommate-country-dropdown roommate-edit-dropdown"
                              :disabled="loading || submitted"
                              @change="onEditCountryCodeChange" />
                </div>
                <div class="roommate-row">
                    <span class="roommate-label">Phone Number</span>
                    <input v-model="editForm.phoneNumber"
                           type="text"
                           inputmode="numeric"
                           class="roommate-edit-input"
                           :placeholder="editPhonePlaceholder"
                           :disabled="loading || submitted"
                           @input="onEditPhoneInput" />
                </div>
                <div class="roommate-row no-border">
                    <span class="roommate-label">Asset</span>
                    <span class="roommate-value">
                        {{ valueOrDash(prefilled.assetName) }}
                    </span>
                </div>
                <div v-if="editError" class="roommate-phone-error roommate-edit-error">
                    {{ editError }}
                </div>
            </div>
            <div class="roommate-card-actions">
                <button type="button"
                        class="btn-primary-roommate"
                        :disabled="loading || submitted || !isEditFormValid"
                        @click="confirmAdd">
                    {{ loading ? 'Please wait...' : 'Confirm & Add' }}
                </button>
                <button type="button"
                        class="btn-cancel-roommate"
                        :disabled="loading"
                        @click="$emit('cancel')">
                    Cancel
                </button>
            </div>
        </div>
        <!-- LIST -->
        <div v-else-if="mode === 'list'"
             class="roommate-card roommate-list-card">
            <div class="roommate-card-header">
                <div class="roommate-header-text">
                    <div class="roommate-card-title">
                        Your {{ entityLabelPlural }}
                    </div>
                    <div v-if="message"
                         class="roommate-card-subtitle">
                        {{ message }}
                    </div>
                </div>
                <div class="roommate-count roommate-count-orange">
                    {{ roommates.length }}
                </div>
            </div>
            <div v-if="roommates.length"
                 class="roommate-list">
                <div v-for="(roommate, index) in roommates"
                     :key="roommate.shareHistoryId || roommate.ShareHistoryId || roommate.id || index"
                     class="roommate-list-item">
                    <!-- ROOMMATE TOP ROW -->
                    <div class="roommate-list-top">
                        <div class="roommate-list-main">
                            <!-- AVATAR + NAME/BADGES -->
                            <div class="roommate-name-row">
                                <div class="roommate-avatar"
                                     :style="{ background: getAvatarColor(roommate, index) }">
                                    {{ getRoommateInitials(roommate) }}
                                </div>
                                <div class="roommate-name-info">
                                    <div class="roommate-name"
                                         :title="getRoommateName(roommate)">
                                        {{ getRoommateName(roommate) }}
                                    </div>
                                    <div class="roommate-badges">
                                        <span v-if="isPrimaryRoommate(roommate)"
                                              class="primary-badge">
                                            Primary
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="roommate-list-actions">
                            <button type="button"
                                    class="roommate-details-btn"
                                    :disabled="loading"
                                    @click="toggleRoommateDetails(roommate, index)">
                                {{ isRoommateDetailsOpen(roommate, index) ? 'Hide' : 'Details' }}
                                <span class="roommate-details-arrow">
                                    {{ isRoommateDetailsOpen(roommate, index) ? '▲' : '▼' }}
                                </span>
                            </button>
                            <button v-if="!isPrimaryRoommate(roommate)"
                                    type="button"
                                    class="remove-roommate-btn"
                                    :disabled="loading"
                                    @click="requestDelete(roommate)">
                                Remove
                            </button>
                        </div>
                    </div>
                    <!-- ROOMMATE DETAILS -->
                    <div v-if="isRoommateDetailsOpen(roommate, index)"
                         class="roommate-details">
                        <div class="roommate-detail-row">
                            <span class="roommate-detail-label">
                                Email
                            </span>
                            <span class="roommate-detail-value">
                                {{ getRoommateEmail(roommate) }}
                            </span>
                        </div>
                        <div class="roommate-detail-row">
                            <span class="roommate-detail-label">
                                Phone
                            </span>
                            <span class="roommate-detail-value">
                                {{ getRoommatePhone(roommate) }}
                            </span>
                        </div>
                        <div class="roommate-detail-row">
                            <span class="roommate-detail-label">
                                Share
                            </span>
                            <span class="roommate-detail-value"
                                  v-html="getRoommateShare(roommate) || '-'">
                            </span>
                        </div>
                        <div class="roommate-detail-row">
                            <span class="roommate-detail-label">
                                Payment Date
                            </span>
                            <span class="roommate-detail-value">
                                {{ getRoommatePaymentDate(roommate) }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else
                 class="empty-state">
                No {{ entityLabelPlural.toLowerCase() }} found.
            </div>
        </div>
        <!-- SUCCESS / ERROR -->
        <div v-if="mode === 'success' || mode === 'error'"
             class="roommate-card">
            <div class="roommate-card-header"
                 :class="{ 'error-header': resultIsError }">
                <div>
                    <div class="roommate-card-title">
                        {{ resultIsError ? (entityLabel + ' Not Added') : (entityLabel + ' Added') }}
                    </div>
                </div>
            </div>
            <div class="roommate-card-body">
                <div class="roommate-row">
                    <span class="roommate-label">
                        Name
                    </span>
                    <span class="roommate-value">
                        {{ resultFullName }}
                    </span>
                </div>
                <div class="roommate-row">
                    <span class="roommate-label">
                        Email
                    </span>
                    <span class="roommate-value">
                        {{ valueOrDash(resultEmail) }}
                    </span>
                </div>
                <div v-if="resultCountryCode !== '-'"
                     class="roommate-row">
                    <span class="roommate-label">
                        Country Code
                    </span>
                    <span class="roommate-value">
                        {{ resultCountryCode }}
                    </span>
                </div>
                <div v-if="resultPhone !== '-'"
                     class="roommate-row no-border">
                    <span class="roommate-label">
                        Phone Number
                    </span>
                    <span class="roommate-value">
                        {{ resultPhone }}
                    </span>
                </div>
                <div v-if="resultIsError"
                     class="roommate-error">
                    {{ roommateData.error || roommateData.message || 'Please try again.' }}
                </div>
            </div>
            <div class="roommate-result-footer">
                <span>
                    {{ formattedToday }}
                </span>
                <span :class="resultIsError ? 'status-error' : 'status-success'">
                    {{ resultIsError ? 'Not Saved' : 'Saved' }}
                </span>
            </div>
        </div>
        <!-- ROOMMATE CONFIRMATION POPUP -->
        <div v-if="confirmModal.show"
             class="roommate-confirm-overlay"
             @click.self="closeConfirmModal">
            <div class="roommate-confirm-card"
                 @click.stop>
                <div class="roommate-confirm-header">
                    {{ confirmModal.title }}
                </div>
                <div class="roommate-confirm-body">
                    <p class="roommate-confirm-message">
                        {{ confirmModal.message }}
                    </p>
                    <div class="roommate-confirm-actions">
                        <button type="button"
                                class="roommate-confirm-btn roommate-confirm-ok"
                                @click="acceptConfirmModal">
                            Ok
                        </button>
                        <button type="button"
                                class="roommate-confirm-btn roommate-confirm-cancel"
                                @click="closeConfirmModal">
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
    import Dropdown from 'primevue/dropdown';
    import { resolvePhoneLength } from '@/helpers/phoneCountryHelpers.js';
    export default {
        name: 'AddRoomate',
        components: {
            Dropdown
        },
        props: {
            mode: {
                type: String,
                default: 'preview'
            },
            message: {
                type: String,
                default: ''
            },
            roommateData: {
                type: Object,
                default: () => ({})
            },
            countryCodeList: {
                type: Array,
                default: () => []
            },
            selectedCountryCodeText: {
                type: String,
                default: ''
            },
            loading: {
                type: Boolean,
                default: false
            },
            isError: {
                type: Boolean,
                default: false
            }
        },
        emits: [
            'submit',
            'cancel',
            'delete-roommate',
            'toggle-status',
            'phone-submit'
        ],
        data() {
            return {
                phoneForm: {
                    countryCodeId: '',
                    countryCodeText: '',
                    phoneNumber: '',
                },
                // Editable fields for the preview card (Asset is NOT editable)
                editForm: {
                    firstName: '',
                    lastName: '',
                    email: '',
                    countryCodeId: '',
                    countryCodeText: '',
                    phoneNumber: ''
                },
                editError: '',
                phoneError: '',
                submitted: false,
                phoneSubmitted: false,
                expandedRoommateKey: '',
                avatarColorPalette: [
                    '#7c3aed',
                    '#2563eb',
                    '#dc2626',
                    '#d97706',
                    '#059669',
                    '#0891b2',
                    '#db2777',
                    '#4f46e5'
                ],
                confirmModal: {
                    show: false,
                    title: '',
                    message: '',
                    action: '',
                    roommate: null,
                    newStatus: null
                }
            };
        },
        computed: {
            // Detect asset type from localStorage
            assetTypeKey() {
                const t = String(localStorage.getItem('assetType') || '').trim().toLowerCase();
                if (t === 'boat' || t === 'boatdera') return 'boat';
                if (t === 'aircraft' || t === 'plane' || t === 'planedera') return 'aircraft';
                return 'default';
            },
            // Singular label
            entityLabel() {
                if (this.assetTypeKey === 'boat' || this.assetTypeKey === 'aircraft') {
                    return 'Co-owner';
                }
                return 'Roommate';
            },
            // Plural label
            entityLabelPlural() {
                if (this.assetTypeKey === 'boat' || this.assetTypeKey === 'aircraft') {
                    return 'Co-owners';
                }
                return 'Roommates';
            },
            phonePromptMessage() {
                return String(this.message || '')
                    .replace(/\*\*/g, '')
                    .trim();
            },
            requiredPhoneLength() {
                const resolvedLength = Number(
                    resolvePhoneLength(
                        this.phoneForm.countryCodeId,
                        this.countryCodeList || []
                    )
                );
                return resolvedLength > 0
                    ? resolvedLength
                    : 10;
            },
            phonePlaceholder() {
                return this.buildPhonePlaceholder(
                    Number(this.requiredPhoneLength || 10)
                );
            },
            isPhoneComplete() {
                return String(this.phoneForm.phoneNumber || '')
                    .replace(/\D/g, '')
                    .length === Number(this.requiredPhoneLength || 10);
            },
            // Required phone length for the editable preview phone field
            editPhoneLength() {
                const resolvedLength = Number(
                    resolvePhoneLength(
                        this.editForm.countryCodeId,
                        this.countryCodeList || []
                    )
                );
                return resolvedLength > 0
                    ? resolvedLength
                    : 10;
            },
            editPhonePlaceholder() {
                return this.buildPhonePlaceholder(
                    Number(this.editPhoneLength || 10)
                );
            },
            isEditEmailValid() {
                return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                    String(this.editForm.email || '').trim()
                );
            },
            isEditFormValid() {
                const digits = String(this.editForm.phoneNumber || '')
                    .replace(/\D/g, '');
                return (
                    String(this.editForm.firstName || '').trim().length > 0 &&
                    this.isEditEmailValid &&
                    !!this.editForm.countryCodeId &&
                    digits.length === Number(this.editPhoneLength || 10)
                );
            },
            prefilled() {
                return this.roommateData?.prefilled || {};
            },
            normalizedCountryCodes() {
                return (this.countryCodeList || [])
                    .map(item => {
                        const value =
                            item.value ??
                            item.id ??
                            item.Id ??
                            item.countryCodeId ??
                            item.CountryCodeId ??
                            '';
                        const text =
                            item.text ??
                            item.name ??
                            item.Name ??
                            item.countryCode ??
                            item.CountryCode ??
                            item.countryCodeText ??
                            item.CountryCodeText ??
                            '';
                        return {
                            value: String(value || ''),
                            text: String(text || '')
                        };
                    })
                    .filter(item => item.value || item.text);
            },
            selectedCountryCode() {
                if (!this.phoneForm.countryCodeId) {
                    return null;
                }
                return this.normalizedCountryCodes.find(
                    item =>
                        String(item.value) ===
                        String(this.phoneForm.countryCodeId)
                ) || null;
            },
            roommates() {
                if (Array.isArray(this.roommateData?.roommates)) {
                    return this.roommateData.roommates;
                }
                if (Array.isArray(this.roommateData?.data)) {
                    return this.roommateData.data;
                }
                if (Array.isArray(this.roommateData?.items)) {
                    return this.roommateData.items;
                }
                return [];
            },
            previewCountryCode() {
                let country = String(
                    this.prefilled.countryCodeText || ''
                ).trim();
                if (
                    !country ||
                    country === 'Loading…' ||
                    country === 'Loading...'
                ) {
                    country = String(
                        this.selectedCountryCodeText ||
                        this.selectedCountryCode?.text ||
                        this.phoneForm.countryCodeText ||
                        ''
                    ).trim();
                }
                return country || '-';
            },
            previewPhone() {
                let phone = String(
                    this.prefilled.phoneNumber || ''
                ).trim();
                if (!phone) {
                    return '-';
                }
                phone = phone
                    .replace(/^\+\d+\s*/, '')
                    .trim();
                return phone || '-';
            },
            resultIsError() {
                return this.isError || this.mode === 'error';
            },
            resultFullName() {
                const firstName =
                    this.roommateData?.firstName ||
                    this.roommateData?.prefilled?.firstName ||
                    '';
                const lastName =
                    this.roommateData?.lastName ||
                    this.roommateData?.prefilled?.lastName ||
                    '';
                const fullName =
                    `${firstName} ${lastName}`.trim();
                return fullName || '-';
            },
            resultEmail() {
                return (
                    this.roommateData?.email ||
                    this.roommateData?.prefilled?.email ||
                    ''
                );
            },
            resultCountryCode() {
                let country = String(
                    this.roommateData?.countryCodeText ||
                    this.roommateData?.prefilled?.countryCodeText ||
                    ''
                ).trim();
                if (
                    !country ||
                    country === 'Loading…' ||
                    country === 'Loading...'
                ) {
                    country =
                        String(this.selectedCountryCodeText || '').trim() ||
                        String(this.selectedCountryCode?.text || '').trim() ||
                        String(this.phoneForm.countryCodeText || '').trim();
                }
                return country || '-';
            },
            resultPhone() {
                let phone = String(
                    this.roommateData?.phoneNumber ||
                    this.roommateData?.prefilled?.phoneNumber ||
                    ''
                ).trim();
                if (!phone) {
                    return '-';
                }
                phone = phone
                    .replace(/^\+\d+\s*/, '')
                    .trim();
                return phone || '-';
            },
            formattedToday() {
                return new Date().toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
            }
        },
        watch: {
        roommateData: {
            immediate: true,
                deep: true,
                    handler(value) {
                const pre = value?.prefilled || {};

                if (this.mode === 'phone') {
                    this.phoneSubmitted = false;
                }

                // Only overwrite the typed phone when the incoming data really has one.
                // Otherwise a parent update (after Continue) wipes what the user entered.
                const incomingPhone =
                    pre.phoneNumber ||
                    value?.phoneNumber ||
                    '';

                if (incomingPhone) {
                    this.phoneForm.phoneNumber = incomingPhone;
                }

                const incomingCountryCodeId =
                    pre.countryCodeId ||
                    value?.countryCodeId ||
                    '';

                if (incomingCountryCodeId) {
                    this.phoneForm.countryCodeId =
                        incomingCountryCodeId;
                }

                let countryText =
                    pre.countryCodeText ||
                    value?.countryCodeText ||
                    '';

                if (
                    countryText === 'Loading…' ||
                    countryText === 'Loading...'
                ) {
                    countryText = '';
                }

                if (countryText) {
                    this.phoneForm.countryCodeText =
                        countryText;
                } else if (this.selectedCountryCodeText) {
                    this.phoneForm.countryCodeText =
                        this.selectedCountryCodeText;
                }

                // Sync editable preview fields
                this.editForm.firstName = pre.firstName || '';
                this.editForm.lastName = pre.lastName || '';
                this.editForm.email = pre.email || '';

                this.editForm.countryCodeId =
                    incomingCountryCodeId ||
                    this.phoneForm.countryCodeId ||
                    '';

                this.editForm.countryCodeText =
                    countryText ||
                    this.phoneForm.countryCodeText ||
                    '';

                this.editForm.phoneNumber = String(
                    pre.phoneNumber ||
                    value?.phoneNumber ||
                    ''
                ).replace(/^\+\d+\s*/, '').trim();

                this.editError = '';
                this.syncEditCountryCode();
            }
        },
            countryCodeList: {
                immediate: true,
                deep: true,
                handler() {
                    if (!this.normalizedCountryCodes.length) {
                        return;
                    }
                    if (this.phoneForm.countryCodeId) {
                        const existing =
                            this.normalizedCountryCodes.find(
                                item =>
                                    String(item.value) ===
                                    String(this.phoneForm.countryCodeId)
                            );
                        if (existing) {
                            this.phoneForm.countryCodeText =
                                this.phoneForm.countryCodeText ||
                                existing.text ||
                                '';
                            this.syncEditCountryCode();
                            return;
                        }
                    }
                    const us =
                        this.normalizedCountryCodes.find(item =>
                            /\(US\)|\bUS\b/i.test(
                                String(item.text || '')
                            )
                        ) ||
                        this.normalizedCountryCodes.find(item =>
                            String(item.text || '')
                                .trim()
                                .startsWith('+1')
                        );
                    if (us) {
                        this.phoneForm.countryCodeId =
                            us.value;
                        this.phoneForm.countryCodeText =
                            us.text || '';
                    }
                    this.syncEditCountryCode();
                }
            }
        },
        methods: {
            valueOrDash(value) {
                return value || '-';
            },
            // Resolve the editable country code against the loaded list and re-format phone
            syncEditCountryCode() {
                if (!this.normalizedCountryCodes.length) {
                    return;
                }
                let match = null;
                if (this.editForm.countryCodeId) {
                    match = this.normalizedCountryCodes.find(
                        item =>
                            String(item.value) ===
                            String(this.editForm.countryCodeId)
                    ) || null;
                }
                if (!match && this.editForm.countryCodeText) {
                    const text = String(this.editForm.countryCodeText).trim();
                    match = this.normalizedCountryCodes.find(
                        item => String(item.text || '').trim() === text
                    ) || null;
                }
                if (!match && this.phoneForm.countryCodeId) {
                    match = this.normalizedCountryCodes.find(
                        item =>
                            String(item.value) ===
                            String(this.phoneForm.countryCodeId)
                    ) || null;
                }
                if (match) {
                    this.editForm.countryCodeId = match.value;
                    this.editForm.countryCodeText = match.text || '';
                }
                this.reformatEditPhone();
            },
            reformatEditPhone() {
                const length = Number(this.editPhoneLength || 10);
                const digits = String(this.editForm.phoneNumber || '')
                    .replace(/\D/g, '')
                    .slice(0, length);
                this.editForm.phoneNumber =
                    this.formatPhoneByLength(digits, length);
            },
            onEditCountryCodeChange() {
                const selected =
                    this.normalizedCountryCodes.find(
                        item =>
                            String(item.value) ===
                            String(this.editForm.countryCodeId)
                    );
                this.editForm.countryCodeText =
                    selected?.text || '';
                this.editForm.phoneNumber = '';
                this.editError = '';
            },
            onEditPhoneInput(event) {
                const requiredLength =
                    Number(this.editPhoneLength || 10);
                const digits =
                    String(event?.target?.value || '')
                        .replace(/\D/g, '')
                        .slice(0, requiredLength);
                this.editForm.phoneNumber =
                    this.formatPhoneByLength(
                        digits,
                        requiredLength
                    );
                this.editError = '';
            },
            onCountryCodeChange() {
                const selected =
                    this.normalizedCountryCodes.find(
                        item =>
                            String(item.value) ===
                            String(this.phoneForm.countryCodeId)
                    );
                this.phoneForm.countryCodeText =
                    selected?.text || '';
                this.phoneForm.phoneNumber = '';
                this.phoneError = '';
                this.phoneSubmitted = false;
            },
            buildPhonePlaceholder(length) {
                const requiredLength =
                    Number(length || 10);
                if (requiredLength === 10) {
                    return '(000)-000-0000';
                }
                if (requiredLength === 7) {
                    return '000-0000';
                }
                if (requiredLength === 8) {
                    return '0000-0000';
                }
                if (requiredLength === 9) {
                    return '000-000-000';
                }
                if (requiredLength === 11) {
                    return '000-0000-0000';
                }
                if (requiredLength > 0) {
                    return '0'.repeat(requiredLength);
                }
                return 'Phone number';
            },
            formatPhoneByLength(digits, length) {
                const clean =
                    String(digits || '')
                        .replace(/\D/g, '');
                const requiredLength =
                    Number(length || 10);
                if (requiredLength === 10) {
                    if (clean.length > 6) {
                        return `(${clean.slice(0, 3)})-${clean.slice(3, 6)}-${clean.slice(6)}`;
                    }
                    if (clean.length > 3) {
                        return `(${clean.slice(0, 3)})-${clean.slice(3)}`;
                    }
                    if (clean.length > 0) {
                        return `(${clean}`;
                    }
                    return '';
                }
                if (requiredLength === 7) {
                    return clean.length > 3
                        ? `${clean.slice(0, 3)}-${clean.slice(3)}`
                        : clean;
                }
                if (requiredLength === 8) {
                    return clean.length > 4
                        ? `${clean.slice(0, 4)}-${clean.slice(4)}`
                        : clean;
                }
                if (requiredLength === 9) {
                    if (clean.length > 6) {
                        return `${clean.slice(0, 3)}-${clean.slice(3, 6)}-${clean.slice(6)}`;
                    }
                    if (clean.length > 3) {
                        return `${clean.slice(0, 3)}-${clean.slice(3)}`;
                    }
                    return clean;
                }
                if (requiredLength === 11) {
                    if (clean.length > 7) {
                        return `${clean.slice(0, 3)}-${clean.slice(3, 7)}-${clean.slice(7)}`;
                    }
                    if (clean.length > 3) {
                        return `${clean.slice(0, 3)}-${clean.slice(3)}`;
                    }
                    return clean;
                }
                return clean;
            },
            onPhoneInput(event) {
                const requiredLength =
                    Number(this.requiredPhoneLength || 10);
                const digits =
                    String(event?.target?.value || '')
                        .replace(/\D/g, '')
                        .slice(0, requiredLength);
                this.phoneForm.phoneNumber =
                    this.formatPhoneByLength(
                        digits,
                        requiredLength
                    );
                this.phoneError = '';
            },
            submitPhone() {
                this.phoneError = '';
                if (this.phoneSubmitted) {
                    return;
                }
                if (!this.phoneForm.countryCodeId) {
                    this.phoneError =
                        'Please select a country code.';
                    return;
                }
                const requiredLength =
                    Number(this.requiredPhoneLength || 10);
                const digits =
                    String(this.phoneForm.phoneNumber || '')
                        .replace(/\D/g, '');
                if (digits.length !== requiredLength) {
                    const countryLabel =
                        this.selectedCountryCode?.text ||
                        this.phoneForm.countryCodeText ||
                        'selected country';
                    this.phoneError =
                        `Please enter a valid ${requiredLength}-digit phone number for ${countryLabel}.`;
                    return;
                }
                const formattedPhone =
                    this.formatPhoneByLength(
                        digits,
                        requiredLength
                    );
                this.phoneSubmitted = true;
                this.$emit('phone-submit', {
                    countryCodeId:
                        this.phoneForm.countryCodeId,
                    countryCodeText:
                        this.selectedCountryCode?.text ||
                        this.phoneForm.countryCodeText ||
                        '',
                    phoneNumber: formattedPhone
                });
            },
            confirmAdd() {
                if (this.submitted) {
                    return;
                }
                // Validate the editable fields
                if (!String(this.editForm.firstName || '').trim()) {
                    this.editError = 'Please enter a first name.';
                    return;
                }
                if (!this.isEditEmailValid) {
                    this.editError = 'Please enter a valid email address.';
                    return;
                }
                if (!this.editForm.countryCodeId) {
                    this.editError = 'Please select a country code.';
                    return;
                }
                const requiredLength =
                    Number(this.editPhoneLength || 10);
                const editDigits =
                    String(this.editForm.phoneNumber || '')
                        .replace(/\D/g, '');
                if (editDigits.length !== requiredLength) {
                    this.editError =
                        `Please enter a valid ${requiredLength}-digit phone number.`;
                    return;
                }
                const selectedEditCountry =
                    this.normalizedCountryCodes.find(
                        item =>
                            String(item.value) ===
                            String(this.editForm.countryCodeId)
                    );
                // Keep the original phone format if the digits were not changed
                const originalDigits =
                    String(this.prefilled.phoneNumber || '')
                        .replace(/^\+\d+\s*/, '')
                        .replace(/\D/g, '');
                const phoneToSend =
                    originalDigits === editDigits && this.prefilled.phoneNumber
                        ? this.prefilled.phoneNumber
                        : this.formatPhoneByLength(
                            editDigits,
                            requiredLength
                        );
                this.editError = '';
                this.submitted = true;
                this.$emit('submit', {
                    ...this.prefilled,
                    firstName: String(this.editForm.firstName || '').trim(),
                    lastName: String(this.editForm.lastName || '').trim(),
                    email: String(this.editForm.email || '').trim(),
                    phoneNumber: phoneToSend,
                    countryCodeId:
                        this.editForm.countryCodeId ||
                        this.prefilled.countryCodeId ||
                        this.phoneForm.countryCodeId ||
                        '',
                    countryCodeText:
                        selectedEditCountry?.text ||
                        this.editForm.countryCodeText ||
                        (
                            (
                                this.prefilled.countryCodeText &&
                                this.prefilled.countryCodeText !== 'Loading…' &&
                                this.prefilled.countryCodeText !== 'Loading...'
                            )
                                ? this.prefilled.countryCodeText
                                : (
                                    this.selectedCountryCodeText ||
                                    this.selectedCountryCode?.text ||
                                    this.phoneForm.countryCodeText ||
                                    ''
                                )
                        )
                });
            },
            getRoommateName(roommate) {
                return (
                    roommate.name ||
                    roommate.shareholderName ||
                    roommate.roommateName ||
                    `${roommate.firstName || ''} ${roommate.lastName || ''}`.trim() ||
                    '-'
                );
            },
            getRoommateEmail(roommate) {
                return (
                    roommate.email ||
                    roommate.shareholderEmail ||
                    roommate.roommateEmail ||
                    '-'
                );
            },
            getRoommatePhone(roommate) {
                return (
                    roommate.phone ||
                    roommate.phoneNumber ||
                    roommate.shareholderPhoneNumber ||
                    '-'
                );
            },
            getRoommateShare(roommate) {
                return (
                    roommate.shareRank ||
                    roommate.ShareHolder ||
                    roommate.shareHolder ||
                    ''
                );
            },
            isPrimaryRoommate(roommate) {
                return (
                    roommate.isPrimary === true ||
                    roommate.isPrimary === 1 ||
                    roommate.IsPrimaryRoommate === 1 ||
                    roommate.isPrimaryRoommate === 1
                );
            },
            roommateKey(roommate, index) {
                return String(
                    roommate?.shareHistoryId ||
                    roommate?.ShareHistoryId ||
                    roommate?.id ||
                    roommate?.Id ||
                    index
                );
            },
            getRoommateInitials(roommate) {
                const name = String(
                    this.getRoommateName(roommate) || ''
                ).trim();
                if (!name || name === '-') {
                    return '?';
                }
                const parts = name
                    .split(/\s+/)
                    .filter(Boolean);
                if (parts.length >= 2) {
                    return (
                        parts[0].charAt(0) +
                        parts[1].charAt(0)
                    ).toUpperCase();
                }
                return parts[0]
                    .substring(0, 2)
                    .toUpperCase();
            },
            getAvatarColor(roommate, index) {
                const palette = this.avatarColorPalette;
                if (!palette || !palette.length) {
                    return '#0f66b8';
                }
                const key = this.roommateKey(roommate, index);
                let hash = 0;
                for (let i = 0; i < key.length; i++) {
                    hash = key.charCodeAt(i) + ((hash << 5) - hash);
                    hash |= 0;
                }
                const paletteIndex =
                    Math.abs(hash) % palette.length;
                return palette[paletteIndex];
            },
            toggleRoommateDetails(roommate, index) {
                const key =
                    this.roommateKey(
                        roommate,
                        index
                    );
                this.expandedRoommateKey =
                    this.expandedRoommateKey === key
                        ? ''
                        : key;
            },
            isRoommateDetailsOpen(roommate, index) {
                return (
                    this.expandedRoommateKey ===
                    this.roommateKey(
                        roommate,
                        index
                    )
                );
            },
            isRoommateActive(roommate) {
                const value =
                    roommate?.isActive ??
                    roommate?.IsActive ??
                    roommate?.active ??
                    roommate?.Active;
                if (
                    value === undefined ||
                    value === null ||
                    value === ''
                ) {
                    return true;
                }
                return (
                    value === true ||
                    value === 1 ||
                    String(value).toLowerCase() === 'true' ||
                    String(value).toLowerCase() === 'active'
                );
            },
            getRoommatePaymentDate(roommate) {
                return (
                    roommate?.paymentDate ||
                    roommate?.PaymentDate ||
                    roommate?.payment_date ||
                    roommate?.PaymentDateText ||
                    roommate?.createdOn ||
                    roommate?.CreatedOn ||
                    '-'
                );
            },
            showConfirmModal(
                title,
                message,
                action,
                roommate,
                newStatus = null
            ) {
                this.confirmModal = {
                    show: true,
                    title,
                    message,
                    action,
                    roommate,
                    newStatus
                };
            },
            closeConfirmModal() {
                this.confirmModal = {
                    show: false,
                    title: '',
                    message: '',
                    action: '',
                    roommate: null,
                    newStatus: null
                };
            },
            acceptConfirmModal() {
                const action =
                    this.confirmModal.action;
                const roommate =
                    this.confirmModal.roommate;
                const newStatus =
                    this.confirmModal.newStatus;
                this.closeConfirmModal();
                if (!roommate) {
                    return;
                }
                if (action === 'delete') {
                    this.$emit('delete-roommate', {
                        roommate: roommate,
                        assetID:
                            this.roommateData?.assetID ||
                            this.roommateData?.assetId ||
                            ''
                    });
                    return;
                }
                if (action === 'status') {
                    this.$emit('toggle-status', {
                        roommate: roommate,
                        isActive: newStatus,
                        assetID:
                            this.roommateData?.assetID ||
                            this.roommateData?.assetId ||
                            ''
                    });
                }
            },
            requestStatusToggle(roommate) {
                const currentStatus =
                    this.isRoommateActive(roommate);
                const newStatus =
                    !currentStatus;
                const name =
                    this.getRoommateName(roommate);
                this.showConfirmModal(
                    `Change ${this.entityLabel} Status`,
                    `Are you sure you want to mark "${name}" as ${newStatus ? 'active' : 'in-active'}?`,
                    'status',
                    roommate,
                    newStatus
                );
            },
            requestDelete(roommate) {
                const name =
                    this.getRoommateName(roommate);
                this.showConfirmModal(
                    `Delete ${this.entityLabel}`,
                    `Are you sure you want to delete ${this.entityLabel.toLowerCase()} "${name}"?`,
                    'delete',
                    roommate
                );
            }
        }
    };
</script>
<style>
    /* Editable fields in the preview card (right-aligned like the old read-only values) */
    .roommate-edit-input {
        flex: 1;
        min-width: 0;
        max-width: 60%;
        margin-left: 12px;
        padding: 6px 10px;
        text-align: right;
        font: inherit;
        font-weight: 600;
        color: inherit;
        background: rgba(255, 255, 255, 0.7);
        border: 1px solid #d9d4c0;
        border-radius: 6px;
        outline: none;
        box-sizing: border-box;
    }

        .roommate-edit-input:focus {
            border-color: #0f66b8;
        }

        .roommate-edit-input:disabled {
            opacity: 0.6;
        }

    .roommate-edit-dropdown {
        max-width: 60%;
        margin-left: 12px;
    }

    @media (max-width: 480px) {
        .roommate-edit-input {
            max-width: 62%;
            margin-left: 8px;
            padding: 6px 8px;
            font-size: 12px;
        }

        .roommate-edit-dropdown {
            max-width: 62%;
            margin-left: 8px;
        }
    }

    /* Dropdown panel is appended to <body>, so it must be styled unscoped
       and sit above the chatbot window */
    .p-dropdown-panel.roommate-dropdown-panel {
        z-index: 9999999 !important;
        min-width: 180px !important;
        max-width: 240px !important;
    }

        .p-dropdown-panel.roommate-dropdown-panel .p-dropdown-items-wrapper {
            max-height: 210px !important;
        }

        .p-dropdown-panel.roommate-dropdown-panel .p-dropdown-item {
            padding: 8px 10px;
            font-size: 12px;
            white-space: nowrap;
        }
</style>

<style scoped>
    .roommate-flow-wrapper {
        width: 100%;
        max-width: 100%;
        min-width: 0;
        box-sizing: border-box;
        font-family: Outfit, sans-serif;
    }

    /* =========================================================
       COMMON CARD
       ========================================================= */
    .roommate-card {
        width: 100%;
        min-width: 0;
        max-width: 360px;
        background: #ffffff;
        border-radius: 16px;
        overflow: hidden;
        font-size: 13px;
        box-sizing: border-box;
        box-shadow: 0 4px 18px rgba(0, 0, 0, 0.12);
    }

    .roommate-card-header {
        background: linear-gradient(135deg, #0f66b8 0%, #122341 100%);
        color: #ffffff;
        padding: 14px 18px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 10px;
    }

    .roommate-header-text {
        flex: 1;
        min-width: 0;
    }

    .error-header {
        background: linear-gradient(135deg, #991b1b, #dc2626) !important;
    }

    .roommate-card-title {
        margin: 0;
        font-size: 14px;
        font-weight: 700;
        color: #ffffff;
    }

    .roommate-card-subtitle {
        margin: 2px 0 0;
        font-size: 11px;
        line-height: 1.4;
        opacity: 0.8;
        color: #ffffff;
        overflow-wrap: anywhere;
    }

    .roommate-card-body {
        padding: 14px 18px;
    }

    /* =========================================================
       PHONE CARD
       ========================================================= */
    .roommate-phone-card {
        width: 330px;
        max-width: 100%;
        min-width: 0;
        background: #ffffff;
        color: #333333;
        border-radius: 16px;
        overflow: visible;
        font-family: Outfit, sans-serif;
        font-size: 13px;
        box-sizing: border-box;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        padding: 1px;
    }

    .roommate-phone-message {
        width: 100%;
        padding: 12px 16px;
        background: linear-gradient(135deg, #0f66b8 0%, #122341 100%);
        color: #ffffff;
        font-size: 13px;
        font-weight: 600;
        line-height: 1.4;
        box-sizing: border-box;
        border-bottom: 2px solid #f39c12;
        border-radius: 16px 16px 0 0;
    }

    /* Grid: fixed dropdown column + input column that can never overflow */
    .roommate-phone-row {
        display: grid;
        grid-template-columns: 125px minmax(0, 1fr);
        align-items: center;
        gap: 8px;
        width: 100%;
        min-width: 0;
        padding: 16px 16px 10px;
        box-sizing: border-box;
        background: #ffffff;
        border-radius: 0;
    }

    .roommate-country-dropdown {
        width: 125px;
        min-width: 0;
        max-width: 125px;
    }

    .roommate-phone-input {
        display: block;
        width: 100%;
        min-width: 0;
        max-width: 100%;
        height: 42px;
        margin: 0;
        padding: 0 12px;
        border: 1px solid #d7dce2;
        border-radius: 10px;
        background: #ffffff !important;
        color: #1e2d45;
        font-family: Outfit, sans-serif;
        font-size: 14px;
        font-weight: 500;
        line-height: 40px;
        outline: none;
        box-sizing: border-box;
        -webkit-appearance: none;
        appearance: none;
        transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }

        .roommate-phone-input::placeholder {
            color: #9aa5b4;
            font-weight: 400;
            opacity: 1;
        }

        .roommate-phone-input:focus {
            border-color: #0f66b8;
            box-shadow: 0 0 0 3px rgba(15, 102, 184, 0.15);
        }

        .roommate-phone-input:disabled {
            background: #f3f5f8 !important;
            cursor: not-allowed;
        }

    .roommate-phone-error {
        padding: 0 16px;
        margin: 0 0 6px;
        color: #dc2626;
        font-size: 11px;
        line-height: 1.4;
    }

    .roommate-edit-error {
        padding: 0;
        margin-top: 8px;
    }

    .roommate-phone-submit {
        display: block;
        width: calc(100% - 32px);
        height: 44px;
        margin: 6px 16px 16px;
        padding: 0;
        border: none;
        border-radius: 12px;
        background: linear-gradient(135deg, #0f66b8 0%, #122341 100%);
        color: #ffffff;
        font-family: Outfit, sans-serif;
        font-size: 14px;
        font-weight: 700;
        cursor: pointer;
        box-sizing: border-box;
        transition: filter 0.15s ease;
    }

        .roommate-phone-submit:hover:not(:disabled) {
            filter: brightness(1.1);
        }

        .roommate-phone-submit:disabled {
            opacity: 0.45;
            cursor: not-allowed;
        }

    /* =========================================================
       PRIMEVUE DROPDOWN (the box itself lives inside this component;
       the open panel is styled in the unscoped block above)
       ========================================================= */
    :deep(.roommate-country-dropdown.p-dropdown) {
        display: inline-flex;
        align-items: center;
        width: 125px;
        min-width: 0;
        max-width: 125px;
        height: 42px;
        border: 1px solid #d7dce2;
        border-radius: 10px;
        background: #ffffff !important;
        box-shadow: none;
        box-sizing: border-box;
        transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }

    :deep(.roommate-country-dropdown.p-dropdown.p-focus),
    :deep(.roommate-country-dropdown.p-dropdown:not(.p-disabled):hover) {
        border-color: #0f66b8;
    }

    :deep(.roommate-country-dropdown.p-dropdown.p-focus) {
        box-shadow: 0 0 0 3px rgba(15, 102, 184, 0.15);
    }

    :deep(.roommate-country-dropdown .p-dropdown-label) {
        display: flex;
        align-items: center;
        flex: 1;
        min-width: 0;
        height: 100%;
        padding: 0 4px 0 12px;
        font-size: 14px;
        font-weight: 500;
        color: #1e2d45;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    :deep(.roommate-country-dropdown .p-dropdown-trigger) {
        width: 28px;
        min-width: 28px;
        background: transparent;
        color: #64748b;
    }

    /* =========================================================
       SUMMARY ROWS
       ========================================================= */
    .roommate-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 10px;
        padding-bottom: 8px;
        margin-bottom: 8px;
        border-bottom: 1px solid #f0f0f0;
    }

        .roommate-row.no-border {
            border-bottom: none;
            margin-bottom: 0;
            padding-bottom: 0;
        }

    .roommate-label {
        flex-shrink: 0;
        color: #888888;
        font-size: 11px;
        font-weight: 600;
        white-space: nowrap;
    }

    .roommate-value {
        flex: 1;
        min-width: 0;
        color: #1e2d45;
        font-size: 12px;
        font-weight: 600;
        text-align: right;
        overflow-wrap: anywhere;
    }

    /* =========================================================
       SUMMARY ACTIONS
       ========================================================= */
    .roommate-card-actions {
        padding: 0 18px 16px;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .btn-primary-roommate {
        width: 100%;
        min-height: 40px;
        background: linear-gradient(135deg, #0f66b8 0%, #122341 100%);
        color: #ffffff;
        border: none;
        border-radius: 10px;
        padding: 10px;
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
    }

        .btn-primary-roommate:disabled,
        .btn-cancel-roommate:disabled,
        .remove-roommate-btn:disabled {
            opacity: 0.45;
            cursor: not-allowed;
            pointer-events: none;
        }

    .btn-cancel-roommate {
        width: 100%;
        border: none;
        background: transparent;
        color: #aaaaaa;
        font-size: 12px;
        cursor: pointer;
        text-decoration: underline;
        padding: 4px 8px;
    }

    /* =========================================================
       ROOMMATE COUNT
       ========================================================= */
    .roommate-count {
        flex-shrink: 0;
        margin-left: auto;
        min-width: 28px;
        height: 28px;
        padding: 0 7px;
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: 14px;
        display: flex;
        justify-content: center;
        align-items: center;
        box-sizing: border-box;
        color: #ffffff;
        font-size: 11px;
        font-weight: 600;
    }

    .roommate-count-orange {
        background: #f5a000;
        border: 0;
        color: #111827;
        font-weight: 700;
    }

    /* =========================================================
       ROOMMATE LIST
       ========================================================= */
    .roommate-list-card {
        max-width: 360px;
    }

    .roommate-list {
        padding: 0 16px 8px;
    }

    .roommate-list-item {
        display: block;
        padding: 11px 0;
        border-bottom: 1px solid #e5e7eb;
    }

        .roommate-list-item:last-child {
            border-bottom: none;
        }

    /* =========================================================
       ROOMMATE TOP ROW
       ========================================================= */
    .roommate-list-top {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 8px;
    }

    .roommate-list-main {
        flex: 1;
        min-width: 0;
    }

    .roommate-name-row {
        display: flex;
        align-items: center;
        gap: 10px;
        min-width: 0;
    }

    .roommate-avatar {
        flex: 0 0 34px;
        width: 34px;
        height: 34px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #ffffff;
        font-size: 12px;
        font-weight: 700;
        text-transform: uppercase;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
    }

    .roommate-name-info {
        flex: 1;
        min-width: 0;
    }

    .roommate-name {
        color: #1e2d45;
        font-size: 13px;
        line-height: 1.35;
        font-weight: 700;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    /* =========================================================
       BADGES
       ========================================================= */
    .roommate-badges {
        display: flex;
        align-items: center;
        gap: 6px;
        flex-wrap: wrap;
        margin-top: 6px;
    }

    .primary-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-height: 20px;
        padding: 2px 9px;
        box-sizing: border-box;
        background: #ffffff;
        border: 1px solid #b7dfc9;
        border-radius: 12px;
        color: #16794b;
        font-size: 10px;
        font-weight: 600;
        line-height: 1.2;
        white-space: nowrap;
    }

    /* =========================================================
       DETAILS / REMOVE
       ========================================================= */
    .roommate-list-actions {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-shrink: 0;
    }

    .roommate-details-btn {
        border: none;
        background: transparent;
        color: #d78a00;
        padding: 0;
        font-size: 10px;
        font-weight: 700;
        cursor: pointer;
        white-space: nowrap;
    }

        .roommate-details-btn:disabled {
            opacity: 0.45;
            cursor: not-allowed;
        }

    .roommate-details-arrow {
        font-size: 8px;
        margin-left: 2px;
    }

    .remove-roommate-btn {
        background: transparent;
        border: none;
        color: #dc2626;
        padding: 0;
        font-size: 10px;
        font-weight: 600;
        cursor: pointer;
        white-space: nowrap;
    }

        .remove-roommate-btn:disabled {
            opacity: 0.45;
            cursor: not-allowed;
        }

    /* =========================================================
       EXPANDED ROOMMATE DETAILS
       ========================================================= */
    .roommate-details {
        margin-top: 10px;
        padding: 9px 10px;
        background: #f8fafc;
        border: 1px solid #dbe3ec;
        border-radius: 7px;
        box-sizing: border-box;
        max-width: 100%;
    }

    .roommate-detail-row {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        padding: 6px 0;
        font-size: 11px;
        line-height: 1.35;
        border-bottom: 1px solid #e5e7eb;
    }

        .roommate-detail-row:last-child {
            border-bottom: none;
            padding-bottom: 2px;
        }

    .roommate-detail-label {
        width: 82px;
        min-width: 82px;
        color: #64748b;
        font-size: 11px;
        font-weight: 600;
    }

    .roommate-detail-value {
        flex: 1;
        min-width: 0;
        color: #1e2d45;
        font-size: 11px;
        font-weight: 500;
        overflow-wrap: anywhere;
    }

    /* =========================================================
       EMPTY STATE
       ========================================================= */
    .empty-state {
        text-align: center;
        padding: 18px;
        color: #999999;
        font-size: 12px;
    }

    /* =========================================================
       ERROR
       ========================================================= */
    .roommate-error {
        display: flex;
        gap: 8px;
        margin-top: 8px;
        padding: 10px;
        background: #fef2f2;
        border: 1px solid #fecaca;
        border-radius: 8px;
        color: #dc2626;
        font-size: 12px;
    }

    /* =========================================================
       RESULT FOOTER
       ========================================================= */
    .roommate-result-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-top: 1px solid #f0f0f0;
        padding: 10px 18px;
        color: #64748b;
        font-size: 11px;
    }

    .status-success {
        color: #16a34a;
        font-weight: 600;
    }

    .status-error {
        color: #dc2626;
        font-weight: 600;
    }

    /* =========================================================
       CONFIRMATION POPUP
       ========================================================= */
    .roommate-confirm-overlay {
        position: fixed;
        inset: 0;
        z-index: 999999;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        box-sizing: border-box;
        background: rgba(0, 0, 0, 0.48);
    }

    .roommate-confirm-card {
        width: min(830px, calc(100vw - 40px));
        max-width: 830px;
        background: #ffffff;
        border-radius: 3px;
        overflow: hidden;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.28);
        text-align: left;
    }

    .roommate-confirm-header {
        min-height: 42px;
        display: flex;
        align-items: center;
        padding: 10px 14px;
        box-sizing: border-box;
        background: #132a4e;
        color: #ffffff;
        font-size: 16px;
        font-weight: 600;
    }

    .roommate-confirm-body {
        padding: 20px 14px 10px;
        background: #ffffff;
    }

    .roommate-confirm-message {
        margin: 0;
        color: #222222;
        font-size: 14px;
        line-height: 1.5;
    }

    .roommate-confirm-actions {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 9px;
        margin-top: 20px;
    }

    .roommate-confirm-btn {
        min-width: 90px;
        height: 34px;
        padding: 6px 16px;
        border: 0;
        border-radius: 3px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
    }

    .roommate-confirm-ok {
        background: #132a4e;
        color: #ffffff;
    }

    .roommate-confirm-cancel {
        background: #cc3b30;
        color: #ffffff;
    }

    /* =========================================================
       MOBILE
       The chat bubble sits next to the bot avatar, so the card must
       never be wider than the space that is really left on screen.
       ========================================================= */
    @media (max-width: 480px) {
        .roommate-card,
        .roommate-list-card {
            min-width: 0;
            width: 100%;
            max-width: min(330px, calc(100vw - 130px));
        }

        /* Phone card gets a DEFINITE width so the row inside can't overflow */
        .roommate-phone-card {
            min-width: 0;
            width: min(330px, calc(100vw - 130px));
            max-width: min(330px, calc(100vw - 130px));
        }

        .roommate-card-header {
            padding: 12px 14px;
        }

        .roommate-card-body {
            padding: 12px 14px;
        }

        .roommate-card-actions {
            padding: 0 14px 14px;
        }

        .roommate-result-footer {
            padding: 10px 14px;
        }

        .roommate-list {
            padding: 0 12px 8px;
        }

        /* Stack Details / Remove vertically so the name gets more room */
        .roommate-list-top {
            gap: 6px;
        }

        .roommate-list-actions {
            flex-direction: column;
            align-items: flex-end;
            gap: 6px;
        }

        .roommate-name-row {
            gap: 8px;
        }

        .roommate-avatar {
            flex: 0 0 30px;
            width: 30px;
            height: 30px;
            font-size: 11px;
        }

        .roommate-details {
            padding: 8px;
        }

        .roommate-detail-row {
            gap: 6px;
        }

        .roommate-detail-label {
            width: 64px;
            min-width: 64px;
        }

        .roommate-phone-message {
            padding: 12px;
        }

        .roommate-phone-row {
            grid-template-columns: 96px minmax(0, 1fr);
            padding: 14px 12px 8px;
            gap: 6px;
        }

        .roommate-phone-input {
            padding: 0 6px;
            font-size: 12px;
            letter-spacing: 0;
        }

        .roommate-phone-error {
            padding: 0 12px;
        }

        .roommate-phone-submit {
            width: calc(100% - 24px);
            margin: 4px 12px 14px;
        }

        .roommate-confirm-overlay {
            padding: 12px;
        }

        .roommate-confirm-card {
            width: 100%;
        }

        .roommate-confirm-header {
            font-size: 14px;
        }

        .roommate-confirm-message {
            font-size: 13px;
        }

        .roommate-country-dropdown {
            width: 96px;
            min-width: 0;
            max-width: 96px;
        }

        :deep(.roommate-country-dropdown.p-dropdown) {
            width: 96px;
            min-width: 0;
            max-width: 96px;
        }

        :deep(.roommate-country-dropdown .p-dropdown-label) {
            padding: 0 2px 0 8px;
            font-size: 12px;
        }

        :deep(.roommate-country-dropdown .p-dropdown-trigger) {
            width: 24px;
            min-width: 24px;
        }

        /* In the preview card the dropdown sits inside a row, let it shrink */
        :deep(.roommate-edit-dropdown.p-dropdown) {
            width: auto;
            min-width: 0;
            flex: 1;
        }
    }
</style>